import { useCallback } from "react"
import { StyleSheet, TextInput, TextStyle, TouchableOpacity, View, ViewStyle } from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"

import { Text } from "src/components/Ignite/Text"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

export { searchItems } from "./contentLibraryUtils"

interface ContentLibrarySearchBarProps {
  query: string
  onQueryChange: (q: string) => void
  placeholder?: string
  /** When provided, a "Cancel" button is shown that clears + collapses the bar */
  onCancel?: () => void
}

/**
 * Client-side title search for the Content Library.
 * Filters by title string match. Search is independent of sort/filter (spec slide 9).
 */
export function ContentLibrarySearchBar({
  query,
  onQueryChange,
  placeholder = "Search",
  onCancel,
}: ContentLibrarySearchBarProps) {
  const { themed, theme } = useAppTheme()
  const { colors } = theme

  const handleClear = useCallback(() => {
    onQueryChange("")
  }, [onQueryChange])

  const handleCancel = useCallback(() => {
    onQueryChange("")
    onCancel?.()
  }, [onQueryChange, onCancel])

  return (
    <View style={styles.row}>
      <View style={[themed($container), { flex: 1 }]}>
        <MaterialIcons name="search" size={20} color={colors.textDim} style={styles.searchIcon} />
        <TextInput
          style={themed($input)}
          value={query}
          onChangeText={onQueryChange}
          placeholder={placeholder}
          placeholderTextColor={colors.textDim}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="search"
          clearButtonMode="never"
          autoFocus
        />
        {query.length > 0 && (
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <MaterialIcons name="close" size={18} color={colors.textDim} />
          </TouchableOpacity>
        )}
      </View>

      {onCancel && (
        <TouchableOpacity
          onPress={handleCancel}
          style={styles.cancelButton}
          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
        >
          <Text style={themed($cancelText)}>Cancel</Text>
        </TouchableOpacity>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 4,
    gap: 8,
  },
  searchIcon: {
    marginRight: 6,
  },
  cancelButton: {
    paddingHorizontal: 4,
    paddingVertical: 6,
    flexShrink: 0,
  },
})

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.background,
  borderRadius: 100,
  paddingHorizontal: 10,
  paddingVertical: 6,
  borderWidth: 1,
  borderColor: colors.pillUnselected,
})

const $cancelText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 15,
  fontFamily: typography.primary.normal,
  color: colors.text,
})

const $inputWebStyle = {
  outlineStyle: "none",
  boxShadow: "none",
} as unknown as TextStyle

const $input: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  flex: 1,
  fontSize: 14,
  fontFamily: typography.primary.normal,
  color: colors.text,
  paddingVertical: 0,
  ...$inputWebStyle,
})
